import { useState } from "react";
import { Navigate } from "react-router-dom";
import { HardHat, FileSignature, ShoppingCart, Warehouse, FileSpreadsheet, ReceiptIndianRupee } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

const IMG = "/login-bg.jpg";
const FEATURES = [[FileSignature, "Contract awards"], [ShoppingCart, "RFQ & POs"], [Warehouse, "GRN & stock"], [FileSpreadsheet, "RA billing"], [ReceiptIndianRupee, "3-way match"]];

export default function Login() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <img src={IMG} alt="Modern residential towers" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-petrol-900/95 via-petrol-900/70 to-blue-900/30" />
      <div className="relative mx-auto flex min-h-screen max-w-7xl flex-col items-center justify-center gap-10 px-5 py-10 lg:flex-row lg:justify-between lg:px-12">
        <div className="max-w-xl text-white fade-up">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-sky-600 shadow-lg shadow-blue-600/40"><HardHat className="h-5 w-5 text-white" /></div>
            <span className="font-heading text-2xl font-bold">P2P Ops</span>
          </div>
          <p className="mt-8 text-xs font-semibold uppercase tracking-[0.2em] text-sky-300">Real estate procure-to-pay</p>
          <h2 className="mt-3 font-heading text-4xl font-bold leading-tight sm:text-5xl">One flow from RFQ to Payment.</h2>
          <p className="mt-4 max-w-md text-sm text-slate-200">Contracts, purchase, stores, QS billing and finance working on the same live data for every project site.</p>
          <div className="mt-8 hidden flex-wrap gap-2 sm:flex">
            {FEATURES.map(([Icon, t]) => (
              <span key={t} className="flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs backdrop-blur"><Icon className="h-3.5 w-3.5 text-sky-300" />{t}</span>
            ))}
          </div>
        </div>
        <form onSubmit={submit} className="w-full max-w-sm rounded-2xl border border-white/40 bg-white/95 p-7 shadow-2xl backdrop-blur-xl fade-up" data-testid="login-form">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Sign in</h1>
          <p className="mt-1 text-sm text-slate-500">Accounts are issued by your administrator.</p>
          <div className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" required value={password} onChange={(e) => setPassword(e.target.value)} testId="login-password-input" />
            </div>
            {error && <p className="text-sm text-rose-600" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">{busy ? "Signing in..." : "Sign in"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}
